'use strict';

import SearchRoutes from "~/components/SearchRoutes";
import NoItems from "~/components/search/NoItems";
import Tooltip from "~/components/Tooltip";
import SearchLayout, { AfterItemsSlot, BeforeFilterSlot, BeforeItemsSlot, ControlSlot, DirectionSlot, ItemsNotFoundSlot, NoItemsSlot } from "~/components/search/SearchLayout";
import SearchListItem, { ControlsSlot, TitleSlot } from "~/components/search/SearchListItem";
import SearchItemCompareButton from "~/components/search/SearchItemCompareButton";
import withQueryClient from "~/helpers/withQueryClient";
import useAircraftCompare from "~/hooks/useAircraftCompare";
import { createRoutesValidator } from "~/helpers/validators";
import SearchSubscribeButton from "../components/SearchSubscribeButton";
import { defaultRoutes, getRoutesFormData, queryGetActualSearchData } from "~/helpers/search";
import SearchSwitchTriple from "~/components/SearchSwitchTriple";
import EventsSlider from "~/components/search/EventsSlider";
import SearchRouteStub from "~/components/search/SearchRouteStub";
import useSubscribeAction from "../hooks/useSubscribeAction";
import useSearchBarometer from "../hooks/useSearchBarometer";
import useSearchRelated from "../hooks/useSearchRelated";
import useSearchAirports from "../hooks/useSearchAirports";
import AircraftCompareButton from "../components/AircraftCompareButton";
import SearchBarometerButton from "../components/SearchBarometerButton";
import SearchItemBarometerButton from "~/components/search/SearchItemBarometerButton";
import OnelineText from "~/components/OnelineText";

const IMAGE_DIMENSIONS = {
    LIST: '260x160',
    DETAIL: '550x360',
}

const routesValidator = createRoutesValidator()

function SearchWidget() {
    const layoutRef = React.useRef(null)

    const [selectedRoutes, setSelectedRoutes] = React.useState([...defaultRoutes])
    const [routeErrors, setRouteErrors] = React.useState(null)
    const [showRouteErrors, setShowRouteErrors] = React.useState(false)

    const { items: aircraftCompareItems, isItemCompareInitiator, isItemInCompare, toggleItem, openCompareModal } = useAircraftCompare()
    const { subscribe } = useSubscribeAction({ routes: selectedRoutes })

    const [seatsAnchor, setSeatsAnchor] = React.useState()
    const [toursCountry, setToursCountry] = React.useState()

    const [planes, setPlanes] = React.useState([])
    const [tours, setTours] = React.useState([])
    const [seats, setSeats] = React.useState([])
    const [searchError, setSearchError] = React.useState(null)
    const [routeStub, setRouteStub] = React.useState(null)

    const abortControllerRef = React.useRef(new AbortController())

    const [requestUUID, setRequestUUID] = React.useState(null)
    const [isPlanesFetching, setIsPlanesFetching] = React.useState(false)
    const [isPlanesLoaded, setIsPlanesLoaded] = React.useState(false)
    const [isSeatsFetching, setIsSeatsFetching] = React.useState(false)
    const [isSeatsLoaded, setIsSeatsLoaded] = React.useState(false)
    const [isToursFetching, setIsToursFetching] = React.useState(false)
    const [isToursLoaded, setIsToursLoaded] = React.useState(false)
    const [searchPerformed, setSearchPerformed] = React.useState(false)
    const [isRoutesOpened, setIsRoutesOpened] = React.useState(false)

    const { data: airports } = useSearchAirports()
    const { data: related, isSuccess: isRelatedSuccess, refetch: fetchRelated } = useSearchRelated(selectedRoutes, { enabled: false })
    const { barometer, aircraftBarometer, isLoaded: isBarometerLoaded, refetch: fetchBarometer, checkItemPriceBelowAnnual, openBarometerModal } = useSearchBarometer(selectedRoutes, planes, { enabled: false })
    const isSelectedRoutesCompleted = selectedRoutes.every(({ from, to, date, time, seats }) => {
        return from && from.id && from.type
            && to && to.id && to.type
            && !!date && !!time && !!seats
    })

    React.useEffect(() => {
        setInitRoutes()
    }, [])

    React.useEffect(() => {
        if (!searchPerformed && getRoutesFormData(selectedRoutes).length > 0) {
            reloadData()
        }
    }, [searchPerformed, selectedRoutes])

    React.useEffect(() => {
        if (isPlanesLoaded) {
            // Если одинаковые equality_key, то берем тот, у кого цена меньше
            const newItems = [];
            planes.forEach((item) => {

                const index = newItems.findIndex((e) => e.equality_key === item.equality_key);

                // Если нет equality_key или еще нет элемента в массиве
                if (!item.equality_key || index === -1) {
                    newItems.push(item);
                    return;
                }

                // Если цена нового элемента меньше - заменяем
                if (newItems[index].prices.price_sort > item.prices.price_sort)
                    newItems.splice(index, 1, item);
            });

            setPlanes(newItems)
            searchAction.saveStatistics({ uuid: requestUUID, type: 'planes', results_count: newItems.length, routes: selectedRoutes });
        }
    }, [isPlanesLoaded])

    React.useEffect(() => {
        if (isSeatsLoaded) {
            storageAdd('reactSearchData', {
                seatsCount: seats.length,
            })
        }
    }, [isSeatsLoaded])

    React.useEffect(() => {
        if (isToursLoaded) {
            storageAdd('reactSearchData', {
                toursCount: tours.length,
            })
        }
    }, [isToursLoaded])

    async function setInitRoutes() {
        const querySearchData = await queryGetActualSearchData(true)
        const reactSearchData = window.storageGetActualSearchData();

        if (querySearchData && querySearchData.length > 0) {
            setSelectedRoutes(querySearchData)
        } else {
            const searchOnMount = Object.keys(reactSearchData).length > 0;
            if (searchOnMount) {
                setLinksData(reactSearchData.routesPlane);
                setSelectedRoutes(reactSearchData.routesPlane || defaultRoutes)
            }
        }
    }

    function resetSearchResults() {
        layoutRef.current.clearFilters()

        setPlanes([])
        setSeats([])
        setTours([])
        setSearchError(null)
        setRouteStub(null)
    }

    function handleSearch() {
        routesValidator.validate(selectedRoutes)
        setRouteErrors(routesValidator.errors)
        if (routesValidator.hasErrors)
            return

        storageRemove('reactSearchData');
        storageSet('reactSearchData', { routesPlane: selectedRoutes, seatsAnchor, toursCountry })

        resetSearchResults()

        setIsPlanesFetching(true)
        setIsPlanesLoaded(false)

        setIsSeatsFetching(true)
        setIsSeatsLoaded(false)

        setIsToursFetching(true)
        setIsToursLoaded(false)

        setSearchPerformed(true)
        setIsRoutesOpened(true)

        abortControllerRef.current.abort()
        abortControllerRef.current = new AbortController()
        const abortSignal = abortControllerRef.current.signal;

        const requestUUID = uuidv4()
        setRequestUUID(requestUUID)
        const fetch = (type, routes, abortSignal) => {
            return new Promise((resolve, reject) => {
                searchAction.fetchStream({
                    onSources: (response, request) => {
                        if (request.type === 'planes') {
                            setRouteStub(response.stub || null)
                            return !!response.stub
                        }
                    },
                    onData: ({ items, payload }, request) => {
                        const setter = {
                            'planes': setPlanes,
                            'seats': setSeats,
                            'tours': setTours,
                        }[request.type] || (() => { })

                        setter(state => {
                            const currentItems = [...state].filter(e => e._cursor !== request.id);

                            for (const item of items) {
                                const found = currentItems.find(e => e.id === item.id)
                                if (found) continue
                                currentItems.push({ ...item, _cursor: request.id })
                            }

                            return currentItems
                        })
                    },
                    onError: (error) => {
                        setSearchError(tMsg('Произошла ошибка при загрузке данных'))
                        console.error(error.message)
                        return reject()
                    },
                    onClose: (cancelled) => {
                        if (cancelled)
                            return reject()

                        resolve()
                    },
                    abortSignal,
                }, { version: 'v2', type, routes, uuid: requestUUID })

            })
        }

        const promises = [
            fetch('planes', selectedRoutes, abortSignal)
                .then(() => setIsPlanesLoaded(true))
                .finally(() => !abortSignal.aborted && setIsPlanesFetching(false))
            ,
            fetch('seats', selectedRoutes, abortSignal)
                .then(() => setIsSeatsLoaded(true))
                .finally(() => !abortSignal.aborted && setIsSeatsFetching(false))
            ,
            fetch('tours', selectedRoutes, abortSignal)
                .then(() => setIsToursLoaded(true))
                .finally(() => !abortSignal.aborted && setIsToursFetching(false))
        ]

        Promise.allSettled(promises)
            .then(() => setStorageAfter())
            .then(() => fetchRelated())
            .then(() => fetchBarometer())
    }


    function reloadData() {
        layoutRef.current.clearFilters();
        layoutRef.current.scrollTop(600);
        handleSearch()
    }

    function setLinksData(routes) {

        if (!routes && !Array.isArray(routes)) return;

        const seatsAnchor = [parseInt(routes[0]['from']['city_id']), parseInt(routes[0]['to']['city_id'])]
            .filter(e => e)
            .sort((a, b) => a - b)
            .join('-');
        const toursCountry = routes[0]['to']['country_id'] || null;

        setSeatsAnchor(seatsAnchor)
        setToursCountry(toursCountry)
    }

    function setStorageAfter() {
        storageSet('reactSearchData', {
            routesPlane: selectedRoutes,

            seatsAnchor: seatsAnchor,
            toursCountry: toursCountry,

            planesCount: planes.length,
            seatsCount: seats.length,
            toursCount: tours.length,
        })
    }

    function handleChangeRoutes(routes) {
        layoutRef.current.scrollTop(600)
        setShowRouteErrors(false)
        setSelectedRoutes(routes)
        setLinksData(routes)
    }

    function handleRoutesFocus() {
        layoutRef.current.scrollTop(600)
    }

    function handleIsOpenedChange(value) {
        layoutRef.current.scrollTop(600)
        setIsRoutesOpened(value)
    }

    function handleItemClick(item) {
        const barometer = {
            averagePrice: (aircraftBarometer || []).find(e => e.aircraft.id == item.aircraft.id)?.average_price,
            onShowBarometerClick: () => openBarometerModal(item.aircraft.id),
        }
        $.fancybox.open({
            src: '#reactFlightSearchModal',
            afterShow: () => {
                raiseEvent('react.selectFlightOffer', {
                    item,
                    barometer,
                    showForm: item.is_emptyleg,
                    buttons: ['submit'],
                    routes: selectedRoutes,
                    imageDimensions: IMAGE_DIMENSIONS.DETAIL
                });
            },
            afterClose: () => {
                raiseEvent('react.selectFlightOffer', { item: null, barometer: null });
            }
        })
    }

    const renderItem = React.useCallback(item => (
        <SearchListItem key={item.id}
            item={item}
            imageSize={IMAGE_DIMENSIONS.LIST}
            onClick={() => handleItemClick(item)}
        >
            <TitleSlot>
                <SearchItemCompareButton active={isItemInCompare(item.aircraft.id)}
                    disabled={!isItemCompareInitiator(item.aircraft.id, item.id)}
                    onClick={() => toggleItem(item.aircraft, item.id)}
                />
            </TitleSlot>

            <ControlsSlot>
                {isBarometerLoaded && aircraftBarometer.length > 0 && <SearchItemBarometerButton isItemPriceBelowAnnual={checkItemPriceBelowAnnual(item)} onPress={() => openBarometerModal(item.aircraft.id)} />}
            </ControlsSlot>
        </SearchListItem>
    ), [isBarometerLoaded])

    return <React.Fragment>

        <SearchSwitchTriple
            type={'planes'}
            planesCount={planes.length}
            seatsCount={seats.length}
            toursCount={tours.length}
            setSearchScenario={searchPerformed}
        />

        <SearchLayout
            layoutRef={layoutRef}
            items={planes}
            renderItem={renderItem}
            isFetching={isPlanesFetching}
            isLoaded={isPlanesLoaded}
            isError={false}
            error={searchError}
            onRefetch={reloadData}
            emptylegFilterEnabled
            isStickable={selectedRoutes.length === 1 || !isRoutesOpened}
        >
            <DirectionSlot>
                <SearchRoutes
                    type={'planes'}
                    routes={selectedRoutes}
                    airports={airports}
                    maxRoutes={5}
                    dropDownParent={'.search'}
                    onlyAirports={true}
                    maxAirports={8}
                    errors={showRouteErrors ? routeErrors : undefined}
                    isOpenedInit={!searchPerformed}
                    onIsOpenedChange={(value) => handleIsOpenedChange(value)}
                    onFocus={() => handleRoutesFocus()}
                    onChangeRoutes={(routes) => handleChangeRoutes(routes)}
                    onSearch={() => reloadData()}
                />
            </DirectionSlot>

            <BeforeFilterSlot>
                {isRelatedSuccess && <EventsSlider items={related.main_events} />}
                <SearchSubscribeButton routes={selectedRoutes} />
            </BeforeFilterSlot>

            <ControlSlot>
                {!routeStub && isBarometerLoaded && barometer.items.length > 0 && <SearchBarometerButton onPress={openBarometerModal} />}
                <AircraftCompareButton items={aircraftCompareItems} onPress={openCompareModal} />
            </ControlSlot>

            <ItemsNotFoundSlot>
                {!routeStub && <NoItems
                    title={searchError}
                    button={tMsg('Следить за ценой')}
                    disabled={!isSelectedRoutesCompleted}
                    onClick={() => subscribe()}
                />}
            </ItemsNotFoundSlot>

            <NoItemsSlot>
                <NoItems
                    title={searchError}
                    button={tMsg('Следить за ценой')}
                    disabled={!isSelectedRoutesCompleted}
                    onClick={() => subscribe()}
                />
            </NoItemsSlot>

            <BeforeItemsSlot>
                {routeStub && <SearchRouteStub
                    type={routeStub.type}
                    contacts={routeStub.contacts}
                />}
            </BeforeItemsSlot>

            <AfterItemsSlot>

                <div className="el__events">

                    {isRelatedSuccess && related.hotels.slice(0, 1).map(({ id, title, price, image, icon, link }) => {
                        return <div key={`hotels${id}`} className="event-card">
                            <a href={link} className="event-card__link"></a>
                            <div className="event-card__img">
                                <img width={226} height={160} src={image} alt="" style={{ objectFit: 'cover' }} />
                            </div>
                            <div className="event-card__title">
                                <div>
                                    <h3><OnelineText text={title} /></h3>
                                    <div className="event-card__title-info">
                                        <i className={icon}></i>
                                        {price}
                                    </div>
                                </div>
                            </div>
                        </div>
                    })}

                    {isRelatedSuccess && related.events.slice(0, 3).map(({ id, title, date, image, flag, link }) => {
                        return <div key={`events${id}`} className="event-card">
                            <a href={link} className="event-card__link"></a>
                            <div className="event-card__img">
                                <img width={226} height={160} src={image || ''} alt="" style={{ objectFit: 'cover' }} />
                            </div>
                            <div className="event-card__title">
                                <div>
                                    <h3><OnelineText text={title} /></h3>
                                    <span>{date}</span>
                                </div>
                                {flag && <div className="event-card__flag">
                                    <img src={flag} alt="" />
                                </div>}
                            </div>
                        </div>
                    })}
                </div>

            </AfterItemsSlot>
        </SearchLayout>

        <Tooltip />
    </React.Fragment>
}

window.SearchWidget = withQueryClient(SearchWidget)
